/**
 * FT-06e: User Identity Display Helper
 *
 * Formats user identity as "J. Fulton (Super Admin)" for audit compliance.
 * MCA inspectors need to see BOTH the person AND their authority.
 */

// Role labels for display (matches FT-98 role hierarchy)
export const ROLE_LABELS = {
  super_admin: 'Super Admin',
  fleet: 'Fleet Manager',
  skipper: 'Skipper',
  crew: 'Crew'
};

/**
 * Format a user's role for display
 * @param {string} role - Role key (super_admin, fleet, skipper, crew)
 * @returns {string} Human-readable role label
 */
export function formatRoleLabel(role) {
  return ROLE_LABELS[role] || role || '';
}

/**
 * Format a name as shortened form: "Jonathan Fulton" -> "J. Fulton"
 * @param {string} name - Full name
 * @returns {string} Shortened name (first initial + surname)
 */
export function formatShortName(name) {
  if (!name) return 'Unknown';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return name;
  // First initial + remaining parts
  return `${parts[0][0]}. ${parts.slice(1).join(' ')}`;
}

/**
 * Format user identity for display: "J. Fulton (Super Admin)"
 *
 * Accepts multiple input formats:
 * - Object: { name: "Jonathan Fulton", role: "super_admin" }
 * - Object with legacy fields: { closedByName: "...", closedByRole: "..." }
 * - Simple name string with optional role
 *
 * @param {object|string} user - User object or name string
 * @param {string} [role] - Role if user is a string
 * @returns {string} Formatted identity: "J. Fulton (Super Admin)"
 */
export function formatUserIdentity(user, role = null) {
  // Handle null/undefined
  if (!user) return 'Unknown';

  // Handle string input
  if (typeof user === 'string') {
    const shortName = formatShortName(user);
    const roleLabel = formatRoleLabel(role);
    return roleLabel ? `${shortName} (${roleLabel})` : shortName;
  }

  // Handle object input - extract name and role from various field patterns
  const name = user.name || user.closedByName || user.reviewedBy || user.createdByName || 'Unknown';
  const userRole = user.role || user.closedByRole || user.reviewedByRole || user.createdByRole || role;

  const shortName = formatShortName(name);
  const roleLabel = formatRoleLabel(userRole);

  return roleLabel ? `${shortName} (${roleLabel})` : shortName;
}

/**
 * Format user identity for header/profile display (no shortening)
 * "Jonathan Fulton (Super Admin)"
 *
 * @param {object} user - User object with name and role
 * @returns {string} Full name with role in parentheses
 */
export function formatUserIdentityFull(user) {
  if (!user) return 'Unknown';

  const name = user.name || 'Unknown';
  const roleLabel = formatRoleLabel(user.role);

  return roleLabel ? `${name} (${roleLabel})` : name;
}

/**
 * Create audit-ready user identity object
 * Use this when writing sign-off/closure fields
 *
 * @param {object} currentUser - Current user from usePermissions
 * @returns {object} { name, role, formatted }
 */
export function createUserIdentityRecord(currentUser) {
  const name = currentUser?.name || 'Unknown User';
  const role = currentUser?.role || '';

  return {
    name,
    role,
    formatted: formatUserIdentity({ name, role })
  };
}
